import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Alert, Button, DatePicker, Form, Input, InputNumber, Modal, Radio, Space, Table, Tag, Typography, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  createSubscriptionCoupon,
  deactivateSubscriptionCoupon,
  reactivateSubscriptionCoupon,
  updateSubscriptionCoupon,
} from "../redux/adminSlice";

const { Text } = Typography;

const currency = new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" });

const errorText = (err, fallback) =>
  (Array.isArray(err?.errors) && err.errors.join(", ")) || err?.message || fallback;

const couponStatus = (c) => {
  if (!c.isActive) return { label: "Inactive", color: "default" };
  if (c.isExpired) return { label: "Expired", color: "orange" };
  return { label: "Active", color: "green" };
};

const PlanCoupons = ({ plan, onChanged }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const discountType = Form.useWatch("discountType", form);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ discountType: "percent" });
    setModalOpen(true);
  };

  const openEdit = (coupon) => {
    setEditing(coupon);
    form.resetFields();
    form.setFieldsValue({
      code: coupon.code,
      discountType: coupon.discountType,
      percentOff: coupon.percentOff,
      amountOff: coupon.amountOff,
      maxRedemptions: coupon.maxRedemptions,
      expiresAtUtc: coupon.expiresAtUtc ? dayjs(coupon.expiresAtUtc) : null,
    });
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleSubmit = async (values) => {
    const body = { code: values.code.trim(), discountType: values.discountType };
    if (values.discountType === "percent") body.percentOff = values.percentOff;
    else body.amountOff = values.amountOff;
    if (values.maxRedemptions) body.maxRedemptions = values.maxRedemptions;
    if (values.expiresAtUtc) body.expiresAtUtc = values.expiresAtUtc.toISOString();

    setSubmitting(true);
    const thunk = editing ? updateSubscriptionCoupon : createSubscriptionCoupon;
    const result = await dispatch(
      thunk(editing ? { couponId: editing.couponId, ...body } : { subscriptionId: plan.subscriptionId, ...body }),
    );
    setSubmitting(false);

    if (thunk.rejected.match(result)) {
      message.error(errorText(result.payload, "Could not save the coupon. Please try again."));
      return;
    }
    message.success(result.payload?.message || "Coupon saved.");
    closeModal();
    onChanged();
  };

  const runAction = async (thunk, coupon, fallback) => {
    const result = await dispatch(thunk(coupon.couponId));
    if (thunk.rejected.match(result)) {
      message.error(errorText(result.payload, fallback));
      return;
    }
    message.success(result.payload?.message || "Done.");
    onChanged();
  };

  const confirmDeactivate = (coupon) =>
    Modal.confirm({
      title: `Deactivate coupon ${coupon.code}?`,
      content: "Patients will no longer be able to use this code. Patients who already paid keep their discount.",
      okText: "Deactivate",
      okButtonProps: { danger: true },
      onOk: () => runAction(deactivateSubscriptionCoupon, coupon, "Could not deactivate the coupon."),
    });

  const columns = [
    { title: "Code", dataIndex: "code", render: (v) => <Text strong>{v}</Text> },
    {
      title: "Discount",
      key: "discount",
      render: (_, c) => (c.discountType === "percent" ? `${c.percentOff}%` : currency.format(c.amountOff ?? 0)),
    },
    { title: "Max uses", dataIndex: "maxRedemptions", render: (v) => v ?? "Unlimited" },
    {
      title: "Expires",
      dataIndex: "expiresAtUtc",
      render: (v) => (v ? dayjs(v).format("MMM D, YYYY h:mm A") : "Never"),
    },
    {
      title: "Status",
      key: "status",
      render: (_, c) => {
        const { label, color } = couponStatus(c);
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: "",
      key: "actions",
      render: (_, c) => (
        <Space>
          <Button size="small" onClick={() => openEdit(c)}>
            Edit
          </Button>
          {c.isActive && (
            <Button size="small" danger onClick={() => confirmDeactivate(c)}>
              Deactivate
            </Button>
          )}
          {!c.isActive && !c.isExpired && (
            <Button
              size="small"
              onClick={() => runAction(reactivateSubscriptionCoupon, c, "Could not reactivate the coupon.")}
            >
              Reactivate
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "8px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <Text strong>Coupons</Text>
        <Button size="small" icon={<PlusOutlined />} onClick={openCreate}>
          Add coupon
        </Button>
      </div>

      <Table
        rowKey="couponId"
        size="small"
        columns={columns}
        dataSource={plan.coupons || []}
        pagination={false}
        locale={{ emptyText: "No coupons for this plan yet." }}
      />

      <Modal
        open={modalOpen}
        title={editing ? `Edit coupon ${editing.code}` : `Add coupon to "${plan.subscriptionName}"`}
        okText={editing ? "Save changes" : "Add coupon"}
        confirmLoading={submitting}
        onOk={() => form.submit()}
        onCancel={closeModal}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="code"
            label="Code"
            normalize={(v) => (v || "").toUpperCase()}
            rules={[
              { required: true, message: "Please enter a code" },
              { pattern: /^[A-Z0-9]{3,20}$/, message: "3-20 letters or numbers, no spaces or symbols" },
            ]}
          >
            <Input maxLength={20} style={{ width: 240 }} />
          </Form.Item>

          <Form.Item name="discountType" label="Discount type" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio value="percent">Percent off</Radio>
              <Radio value="amount">Fixed amount off (CAD)</Radio>
            </Radio.Group>
          </Form.Item>

          {discountType === "amount" ? (
            <Form.Item
              name="amountOff"
              label={`Amount off (max ${currency.format(plan.totalAmount)})`}
              rules={[
                { required: true, message: "Enter an amount" },
                { type: "number", min: 0.01, message: "Must be greater than 0" },
                {
                  type: "number",
                  max: plan.totalAmount,
                  message: "Cannot be more than the plan total",
                },
              ]}
            >
              <InputNumber min={0} precision={2} style={{ width: 180 }} />
            </Form.Item>
          ) : (
            <Form.Item
              name="percentOff"
              label="Percent off"
              rules={[
                { required: true, message: "Enter a percent" },
                { type: "number", min: 0.01, max: 100, message: "Must be between 0 and 100" },
              ]}
            >
              <InputNumber min={0} max={100} precision={2} addonAfter="%" style={{ width: 180 }} />
            </Form.Item>
          )}

          <Space align="start" size={16}>
            <Form.Item
              name="maxRedemptions"
              label="Max uses (optional)"
              rules={[{ type: "number", min: 1, message: "At least 1" }]}
            >
              <InputNumber min={1} precision={0} placeholder="Unlimited" style={{ width: 160 }} />
            </Form.Item>
            <Form.Item name="expiresAtUtc" label="Expires (optional)">
              <DatePicker
                showTime={{ format: "h:mm A" }}
                format="MMM D, YYYY h:mm A"
                placeholder="Never"
                disabledDate={(d) => d.isBefore(dayjs(), "day")}
              />
            </Form.Item>
          </Space>

          {editing && (
            <Alert
              type="info"
              showIcon
              message="Saving replaces the code in Stripe. Patients who already paid keep their discount, and the use count restarts at 0."
            />
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default PlanCoupons;
